import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Shared client. withCredentials sends/receives the httpOnly admin cookie.
const client = axios.create({ baseURL: API, withCredentials: true });

export const joinWaitlist = async (name, email, source = "landing") => {
  const res = await client.post(`/waitlist`, { name, email, source });
  return res.data;
};

export const getWaitlistCount = async () => {
  const res = await client.get(`/waitlist/count`);
  return res.data.count;
};

// ---- Admin (auth via httpOnly cookie; no token handling in JS) ----
export const adminLogin = async (password) => {
  const res = await client.post(`/admin/login`, { password });
  return res.data;
};

export const adminLogout = async () => {
  const res = await client.post(`/admin/logout`, {});
  return res.data;
};

export const adminVerify = async () => {
  const res = await client.get(`/admin/verify`);
  return res.data;
};

export const adminStats = async () => {
  const res = await client.get(`/admin/stats`);
  return res.data;
};

export const adminWaitlist = async () => {
  const res = await client.get(`/admin/waitlist`);
  return res.data;
};

export const adminChats = async () => {
  const res = await client.get(`/admin/chats`);
  return res.data;
};

export const adminAnalytics = async () => {
  const res = await client.get(`/admin/analytics`);
  return res.data;
};

export const adminLaunch = async () => {
  const res = await client.post(`/admin/launch`, {});
  return res.data;
};

export const adminTestEmail = async (email) => {
  const res = await client.post(`/admin/test-email`, { email });
  return res.data;
};

export const adminEmailPreview = async () => {
  const res = await client.get(`/admin/email-preview`);
  return res.data;
};

export const adminEmailEvents = async () => {
  const res = await client.get(`/admin/email-events`);
  return res.data;
};

const buildExportQuery = (start, end) => {
  const params = new URLSearchParams();
  if (start) params.append("start", start);
  if (end) params.append("end", end);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const triggerBlobDownload = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const adminExportWaitlist = async (start, end) => {
  const qs = buildExportQuery(start, end);
  const res = await client.get(`/admin/waitlist/export${qs}`, { responseType: "blob" });
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const rangePart = start || end ? `-${start || "start"}_to_${end || "end"}` : "";
  triggerBlobDownload(res.data, `jedbot-waitlist-${today}${rangePart}.csv`);
};

// ---- Chat streaming (SSE over fetch) ----
const dispatchSseEvent = (jsonStr, handlers) => {
  let evt;
  try {
    evt = JSON.parse(jsonStr);
  } catch (err) {
    // Malformed/partial SSE chunk — safe to skip.
    return;
  }
  if (evt.type === "delta") handlers.onDelta?.(evt.content);
  else if (evt.type === "error") handlers.onError?.(evt.content);
  else if (evt.type === "done") handlers.onDone?.();
};

const processSseBuffer = (buffer, handlers) => {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop() || "";
  for (const part of parts) {
    const line = part.trim();
    if (!line.startsWith("data:")) continue;
    const jsonStr = line.slice(5).trim();
    if (jsonStr) dispatchSseEvent(jsonStr, handlers);
  }
  return remainder;
};

/**
 * Stream a chat response via SSE. Calls onDelta for each token, onDone when finished,
 * onError on failure. Returns the session id used.
 */
export const streamChat = async ({ message, sessionId, onDelta, onDone, onError }) => {
  const handlers = { onDelta, onDone, onError };
  try {
    const res = await fetch(`${API}/chat/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, session_id: sessionId || null }),
    });

    if (!res.ok || !res.body) {
      throw new Error(`Request failed: ${res.status}`);
    }

    const returnedSession = res.headers.get("X-Session-Id") || sessionId;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = processSseBuffer(buffer, handlers);
    }

    onDone?.();
    return returnedSession;
  } catch (e) {
    onError?.(e.message || "Connection error");
    return sessionId;
  }
};
